/**
 * Icon Candidates
 *
 * Pure data mapping from Studio-facing icon names to candidate export names
 * in icon library modules.
 *
 * IMPORTANT: This file has ZERO imports. It is pure data only.
 */

export type IconName =
  | "alert-circle"
  | "arrow-down"
  | "arrow-left"
  | "arrow-right"
  | "arrow-up"
  | "bell"
  | "calendar"
  | "check"
  | "check-circle"
  | "chevron-down"
  | "chevron-left"
  | "chevron-right"
  | "chevron-up"
  | "clock"
  | "close"
  | "code"
  | "copy"
  | "edit"
  | "eye"
  | "eye-off"
  | "file"
  | "filter"
  | "flag"
  | "folder"
  | "grid"
  | "heart"
  | "help-circle"
  | "home"
  | "image"
  | "info"
  | "key"
  | "layout"
  | "layout-grid"
  | "link"
  | "list"
  | "lock"
  | "log-in"
  | "log-out"
  | "mail"
  | "message-circle"
  | "message-square"
  | "menu"
  | "minus"
  | "more-horizontal"
  | "more-vertical"
  | "package"
  | "plane"
  | "plus"
  | "plus-circle"
  | "plus-square"
  | "refresh"
  | "save"
  | "search"
  | "send"
  | "settings"
  | "share"
  | "shield"
  | "sparkles"
  | "square"
  | "star"
  | "sun"
  | "trash"
  | "triangle"
  | "unlock"
  | "user"
  | "users"
  | "zap"
  | "compass";

export type IconCandidates = Record<IconName, readonly string[]>;

/**
 * Candidate export names are ordered so Lucide-compatible names are preferred
 * for the navigation icons used by the Mneno sidebar. The registry loads
 * Lucide alongside Untitled UI and resolves the first matching export.
 */
export const ICON_CANDIDATES: IconCandidates = {
  "alert-circle": ["CircleAlert", "AlertCircle"],
  "arrow-down": ["ArrowDown"],
  "arrow-left": ["ArrowLeft"],
  "arrow-right": ["ArrowRight"],
  "arrow-up": ["ArrowUp"],
  bell: ["Bell"],
  calendar: ["Calendar"],
  check: ["Check"],
  "check-circle": ["CircleCheck", "CheckCircle"],
  "chevron-down": ["ChevronDown"],
  "chevron-left": ["ChevronLeft"],
  "chevron-right": ["ChevronRight"],
  "chevron-up": ["ChevronUp"],
  clock: ["Clock"],
  close: ["X", "CircleX", "Close", "XClose"],
  code: ["Code", "CodeBrowser", "Code01", "Code02"],
  copy: ["Copy"],
  edit: ["PencilLine", "Pencil", "SquarePen", "Edit"],
  eye: ["Eye"],
  "eye-off": ["EyeOff"],
  file: ["File"],
  filter: ["Filter", "FilterFunnel", "FilterLines"],
  flag: ["Flag"],
  folder: ["Folder"],
  grid: ["Grid", "Grid01", "Grid02"],
  heart: ["Heart"],
  "help-circle": ["CircleHelp", "HelpCircle"],
  home: ["House", "Home", "Home01"],
  image: ["Image"],
  info: ["Info", "InfoCircle"],
  key: ["Key"],
  layout: ["Layout", "LayoutAlt01"],
  "layout-grid": ["LayoutGrid", "LayoutGrid01", "Layout", "Grid"],
  link: ["Link", "Link01", "LinkExternal01"],
  list: ["List"],
  lock: ["Lock", "Lock01"],
  "log-in": ["LogIn", "Login", "LogIn01", "LogIn02"],
  "log-out": ["LogOut", "Logout", "LogOut01"],
  mail: ["Mail", "Mail01"],
  "message-circle": ["MessageCircle", "MessageChatCircle"],
  "message-square": ["MessageSquare", "MessageChatSquare", "MessageCircle"],
  menu: ["Menu", "Menu01"],
  minus: ["Minus"],
  "more-horizontal": ["Ellipsis", "MoreHorizontal", "DotsHorizontal"],
  "more-vertical": ["EllipsisVertical", "MoreVertical", "DotsVertical"],
  package: ["Package"],
  plane: ["Send", "PaperPlane"],
  plus: ["Plus"],
  "plus-circle": ["CirclePlus", "PlusCircle"],
  "plus-square": ["PlusSquare"],
  refresh: ["RefreshCw", "RefreshCcw", "Refresh", "RotateCw", "RotateCcw"],
  save: ["Save"],
  search: ["Search", "SearchLg", "SearchMd", "SearchSm"],
  send: ["Send", "PaperPlane"],
  settings: ["Settings", "Gear"],
  share: ["Share", "Share01", "Share07"],
  shield: ["Shield", "Shield01"],
  sparkles: ["Sparkles", "Sparkle", "Stars"],
  square: ["Square"],
  star: ["Star"],
  sun: ["Sun"],
  trash: ["Trash", "Trash2", "Trash3"],
  triangle: ["Triangle"],
  unlock: ["Unlock", "LockUnlocked01"],
  user: ["User"],
  users: ["Users", "Users01", "Users02", "UsersX"],
  zap: ["Zap"],
  compass: ["Compass"],
};